import { watch, computed, ComputedRef, reactive, ref, Ref } from 'vue';
import { FilterModel, FilterParamEntry } from '@Apps/Search/Src/Models/FilterModel';
import PagingModel from '@Apps/Search/Src/Models/PagingModel';
import * as searchComposables from '@Composables/SearchComposables';

import { addNewHistoryEntry } from '@Apps/Search/Src/Logic/historyLogic';
import { toUrl } from '@Apps/Search/Src/Logic/urlConvert';
import SearchModel from '@Apps/Search/Src//Models/SearchModel';
import SearchOrderingOption from '../Types/search/SearchOrderingOption';


export interface SetupResult {

    stateIsFetching: ComputedRef<boolean>;
    triggerNewSearch: () => void;

}

export interface IFetchSearchResult {
    pageCount: number | undefined;
    page: number | undefined;
    pageSize: number | undefined;
    totalCount: number;
}

const queueDelay = 200;
let _searchModel: SearchModel;
let _onProcessData: (data: IFetchSearchResult) => void;

const fetchQueue = reactive({

    currentTimeout: -1,
    activeFetches: new Map<number, boolean>(),
    newFetchWaiting: false as number | boolean


});


export function setup(searchModel: SearchModel, onProcessData: (data: IFetchSearchResult) => void): SetupResult {


    _searchModel = searchModel;
    _onProcessData = onProcessData;

    /**
      * true if there is a fetch requesting running to get 
      * new search results.
      **/
    const stateIsFetching = computed(() => {

        return isInDebounce() || isFetching() || fetchQueue.newFetchWaiting === true;

    });


    return {

        stateIsFetching,
        triggerNewSearch: changeTriggered
    };
}



function isInDebounce() {

    return fetchQueue.currentTimeout > -1;
}

function isFetching() {

    return fetchQueue.activeFetches.size > 0;
}

function changeTriggered() {

    // this means a change has triggered on the model, and we should fetch new data, (with timeout)
    // but wait for the current fetch to complete, (if any)

    if (isInDebounce()) {

        // extend the debounce by resetting and setting a new timeout
        clearTimeout(fetchQueue.currentTimeout);
        fetchQueue.currentTimeout = setTimeout(fetchNext, queueDelay);
    }
    else if (isFetching()) {

        // already fetching, so we should wait until the fetch is complete
        fetchQueue.newFetchWaiting = Date.now();
    }
    else {

        // first trigger:
        fetchQueue.currentTimeout = setTimeout(fetchNext, queueDelay);
    }
}

export function fetchPage(page: number) {

    // force clear timeout
    clearTimeout(fetchQueue.currentTimeout);
    fetchNext(page);
}


function fetchNext(page?: number) {

    // when fetching the next item, it means that the current timestamp has expired,
    // we add that to the queue
    const fetchId = fetchQueue.currentTimeout;
    fetchQueue.activeFetches.set(fetchId, true);
    fetchQueue.currentTimeout = -1;

    // always create new paging for page 0 unless specifically specified
    const url = toUrl(_searchModel.filter, _searchModel.paging.clone(page === undefined ? 0 : page));
    fetchDataAsync(url, fetchId);
}


/**
 * when a fetch call has ended, either succesfully or with errors, we need to 
 * update our fetch queue.
 */
function fetchEnded(fetchId: number) {

    // we can clear our fetchQueue map:
    fetchQueue.activeFetches.delete(fetchId); // we can check the result value to see if we actually had a fetchId...

    if (fetchQueue.newFetchWaiting !== false) {

        const timeout = fetchQueue.newFetchWaiting as number;

        // set timeout
        let diff = Date.now() - timeout;
        if (diff < 1) {
            diff = 1;
        }

        // should not have a new  timeout, but just in case:
        if (fetchQueue.currentTimeout > -1) {
            clearTimeout(fetchQueue.currentTimeout);
        }

        fetchQueue.currentTimeout = setTimeout(fetchNext, diff);
        fetchQueue.newFetchWaiting = false; // reset
    }
}

export enum CreateSearchUrlType {

    fetch = 0,
    search = 1,
    mapSearch = 2,
    /**
     * current means use whatever is currently active
     */
    current = 3,

    /**
     * Means switch from map to non map, or vice versa. for landingpages
     * this will automatically include the landing page id as part of the url rewrite.
     */
    switch = 4
}


export function createSearchUrl(searchQuery: string, type: CreateSearchUrlType, includePath = true) {


    // determine which url we need..
    // if fetch, then easy, if not, check if map is required.
    let urlAction = searchComposables.getSearchMetaDefinitions().fetchUrl;

    if (type == CreateSearchUrlType.current) {

        if (searchComposables.getSearchMetaDefinitions().isMapSearch) {
            urlAction = searchComposables.getSearchMetaDefinitions().searchUrl;
        }
        else {
            urlAction = searchComposables.getSearchMetaDefinitions().mapSearchUrl;
        }

    }
    else if (type == CreateSearchUrlType.mapSearch) {

        urlAction = searchComposables.getSearchMetaDefinitions().mapSearchUrl;
    }
    else if (type == CreateSearchUrlType.search) {

        urlAction = searchComposables.getSearchMetaDefinitions().searchUrl;
    }
    else if (type == CreateSearchUrlType.switch) {

        urlAction = searchComposables.getSearchMetaDefinitions().switchUrl;
    }


    let url = new URL(urlAction, window.location.href).href;

    if (searchQuery) {

        url = url + '?' + searchQuery;
    }

    if (includePath) {

        url = url + (searchQuery?"&":"?") + (`path=${window.location.pathname}`);
    }

   
    // for switch we do not need these values as the url would include the id / rewritten path.
    if (type != CreateSearchUrlType.switch) {
        if (searchComposables.getSearchMetaDefinitions().landingPageId != null) {

            url += "&landingPageId=" + searchComposables.getSearchMetaDefinitions().landingPageId;
        }

        if (searchComposables.getSearchMetaDefinitions().providerPageId != undefined) {
            url += "&providerId=" + searchComposables.getSearchMetaDefinitions().providerPageId;
        }
    }


    return url;
}

function fetchDataAsync(search: string, fetchId: number) {


    let url = createSearchUrl(search, CreateSearchUrlType.fetch);

    addNewHistoryEntry(search);


    fetch(url,
        {
            method: 'GET',
            redirect: "error"
        })
        .then(response => response.json())

        .then(
            // closure for fetchId so that we can unset/update the fetchQueue
            // (also used for errors)
            (function (_fid: number) {

                return function (response: any) {

                    fetchDataResult(_fid, response);
                }
            })(fetchId))

        .catch(
            // @see then, using closure
            (function (_fid: number) {

                return function (response: any) {

                    fetchDataResultError(_fid, response);
                }
            })(fetchId)

        );
}

function fetchDataResult(fetchId: number, data: IFetchSearchResult) {

    // update content... (note that we should not update the filter
    // as  that is being watched by vue!)

    _searchModel.totalCount = data.totalCount;


    _searchModel.paging.setPage(data.page);
    _searchModel.paging.setPageCount(data.pageCount);
    _searchModel.paging.setPageSize(data.pageSize);

    _onProcessData(data);

    fetchEnded(fetchId);
}

function fetchDataResultError(fetchId: number, response: any) {

    // something went wrong, do we need to show the user?
    // 
    alert('Er ging iets mis bij het ophalen van de informatie, probeer de pagina te verversen');

    fetchEnded(fetchId);
}



export type ISortableAccommodationEntry = {

    searchPrice?: number;
    reviewScore?: number;
    maxGuestCount?: number;
    orderKeyString: string;
}

export function orderSearchResults(orderBy: SearchOrderingOption, hasDatesSelected: boolean, items: Array<ISortableAccommodationEntry>) {

    // we only order by price if we have dates selected..
    if (orderBy == SearchOrderingOption.PriceAsc && hasDatesSelected) {

        return [...items].sort((a, b) => (a.searchPrice ?? 0) - (b.searchPrice ?? 0));



    }
    else if (orderBy == SearchOrderingOption.PriceDesc && hasDatesSelected) {
        return [...items].sort((a, b) => (b.searchPrice ?? 0) - (a.searchPrice ?? 0));

    }
    else if (orderBy == SearchOrderingOption.MaxGuestCountAsc) {
        return [...items].sort((a, b) => (a.maxGuestCount ?? 0) - (b.maxGuestCount ?? 0));
    }
    else if (orderBy == SearchOrderingOption.MaxGuestCountDesc) {
        return [...items].sort((a, b) => (b.maxGuestCount ?? 0) - (a.maxGuestCount ?? 0));
    }

    else {

        return [...items].sort((a, b) => (a.orderKeyString ?? "").localeCompare(b.orderKeyString));
    }
}